import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Check, ChevronLeft, ChevronRight, FileText, Info, Sparkles } from 'lucide-react'
import { toast } from 'sonner'
import { DashboardLayout } from '@/components/layout'
import { Card, CardContent } from '@/components/ui/card'
import { CandidateDetail, type DrawerCandidate } from '@/lib/CandidateDrawer'
import { Markdown } from '@/lib/Markdown'
import { type JobDetail, type JobShortlistCandidate, getJob, selectCandidate } from '@/lib/api'
import { atsNav } from '@/lib/atsNav'
import { Avatar } from '@/lib/atsUi'
import { currentTenant, currentUser } from '@/lib/mocks'

export const Route = createFileRoute('/compare/$jobId/$candidateId')({ component: ComparePage })

const fmtExp = (m: number | null | undefined) => (m == null ? '—' : `${Math.floor(m / 12)}y ${m % 12}m`)
const verdictDot = (v: string | null): string => (v === 'strong' ? 'bg-success' : v === 'weak' ? 'bg-error' : v === 'maybe' ? 'bg-warning' : 'bg-muted-foreground/40')

const toDrawer = (c: JobShortlistCandidate): DrawerCandidate => ({
  candidateId: c.candidateId, name: c.name, employer: c.employer, totalMonthsExp: c.totalMonthsExp, location: c.location,
  verdict: (c.verdict as DrawerCandidate['verdict']) ?? null, score: c.score ?? undefined, confidence: c.confidence,
  rationale: c.rationale ?? undefined, gaps: [], strengths: c.strengths, concerns: c.concerns, pointsToDiscuss: c.pointsToDiscuss,
})

function ComparePage() {
  const { jobId, candidateId } = Route.useParams()
  const navigate = useNavigate()
  const qc = useQueryClient()
  // refetchOnMount: 'always' — a screening on the Live-Screening page changes the shortlist; without this
  // the 60s global staleTime would serve a pre-screening (empty) cache → "No screened candidates yet".
  const { data: job, isFetching } = useQuery({ queryKey: ['job', jobId], queryFn: () => getJob(jobId), refetchOnMount: 'always' })
  const scrollerRef = useRef<HTMLDivElement>(null)
  const [showDebug, setShowDebug] = useState(false)

  const shortlist = job?.shortlist ?? []
  const idx = shortlist.findIndex((c) => c.candidateId === candidateId)
  const active = idx >= 0 ? shortlist[idx] : shortlist[0]
  const selectedCount = shortlist.filter((c) => c.selected).length

  const goTo = useCallback((cid: string) => navigate({ to: '/compare/$jobId/$candidateId', params: { jobId, candidateId: cid } }), [jobId, navigate])
  const prev = idx > 0 ? shortlist[idx - 1] : null
  const next = idx >= 0 && idx < shortlist.length - 1 ? shortlist[idx + 1] : null

  // Optimistic shortlist toggle (instant), then auto-advance to the next candidate — the scan→shortlist→repeat loop.
  const toggleSelect = useCallback(async (advance = false) => {
    if (!active || !job) return
    const nextSel = !active.selected
    qc.setQueryData(['job', jobId], { ...job, shortlist: shortlist.map((c) => (c.candidateId === active.candidateId ? { ...c, selected: nextSel } : c)) })
    try {
      await selectCandidate(jobId, active.candidateId, nextSel)
    } catch {
      toast.error('Could not update selection')
      qc.invalidateQueries({ queryKey: ['job', jobId] })
      return
    }
    if (advance && nextSel && next) setTimeout(() => goTo(next.candidateId), 200)
  }, [active, job, jobId, shortlist, next, goTo, qc])

  // Keyboard: ←/→/↑/↓ (or j/k) move between candidates, S shortlist+advance, X pass+advance, Esc back.
  // ↑/↓ are the primary candidate-stepper (focus is on the candidate profile by default).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return
      if ((e.key === 'ArrowUp' || e.key === 'ArrowLeft' || e.key === 'k') && prev) { e.preventDefault(); goTo(prev.candidateId) }
      else if ((e.key === 'ArrowDown' || e.key === 'ArrowRight' || e.key === 'j') && next) { e.preventDefault(); goTo(next.candidateId) }
      else if (e.key.toLowerCase() === 's') { e.preventDefault(); toggleSelect(true) }
      else if (e.key.toLowerCase() === 'x' && next) { e.preventDefault(); goTo(next.candidateId) }
      else if (e.key === 'Escape') navigate({ to: '/jobs/$jobId', params: { jobId } })
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [prev, next, goTo, toggleSelect, navigate, jobId])

  // Keep the active chip centered as the recruiter arrows through.
  useEffect(() => {
    scrollerRef.current?.querySelector('[data-active="true"]')?.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' })
  }, [candidateId])

  if (!job) return <DashboardLayout title="Compare" navSections={atsNav} tenant={currentTenant} user={currentUser}><div className="p-6 text-sm text-muted-foreground">Loading…</div></DashboardLayout>
  if (!active) return <DashboardLayout title="Compare" navSections={atsNav} tenant={currentTenant} user={currentUser}><div className="p-6 text-sm text-muted-foreground">{isFetching ? 'Loading…' : 'No screened candidates yet for this role.'}</div></DashboardLayout>

  const must = job.requirements.mustHave ?? []
  const nice = job.requirements.niceToHave ?? []
  const candSkills = (active.skills ?? []).map((s) => s.toLowerCase())
  const matches = (skill: string) => candSkills.some((cs) => cs.includes(skill.toLowerCase()) || skill.toLowerCase().includes(cs))

  return (
    <DashboardLayout title="Compare" navSections={atsNav} tenant={currentTenant} user={currentUser}>
      <div className="flex h-[calc(100vh-7.5rem)] flex-col">
        {/* Sticky top bar: job + horizontal candidate scroller + progress */}
        <div className="border-b border-border bg-background pb-2">
          <div className="mb-2 flex flex-wrap items-center gap-3">
            <Link to="/jobs/$jobId" params={{ jobId }} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="h-3.5 w-3.5" /> {job.requirements.title}</Link>
            <span className="text-xs text-muted-foreground">{idx + 1} of {shortlist.length} · <span className="font-medium text-teal-600">{selectedCount} shortlisted</span></span>
            <span className="ml-auto hidden text-[11px] text-muted-foreground sm:block"><kbd className="rounded border border-border px-1">↑↓</kbd> switch · <kbd className="rounded border border-border px-1">S</kbd> shortlist · <kbd className="rounded border border-border px-1">Esc</kbd> back</span>
          </div>
          <div ref={scrollerRef} className="flex gap-1.5 overflow-x-auto pb-1.5">
            {shortlist.map((c, i) => {
              const on = c.candidateId === active.candidateId
              return (
                <button key={c.candidateId} data-active={on} onClick={() => goTo(c.candidateId)}
                  className={`flex shrink-0 items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs transition-colors ${on ? 'border-primary bg-primary/10 text-foreground' : 'border-border bg-card text-muted-foreground hover:bg-muted/50'}`}>
                  <span className="tabular-nums opacity-60">{i + 1}</span>
                  <span className={`h-1.5 w-1.5 rounded-full ${verdictDot(c.verdict)}`} />
                  <span className="max-w-[120px] truncate font-medium">{c.name ?? '—'}</span>
                  <span className="tabular-nums font-semibold text-foreground">{c.score}</span>
                  {c.selected && <Check className="h-3 w-3 text-teal-600" />}
                </button>
              )
            })}
          </div>
        </div>

        {/* Two equal-width, independently-scrolling columns: JD (left) vs candidate (right) */}
        <div className="grid flex-1 grid-cols-1 overflow-hidden lg:grid-cols-2">
          {/* LEFT — JD + candidate-aware must-have checklist */}
          <div className="overflow-y-auto border-border lg:border-r">
            <div className="sticky top-0 z-10 flex h-12 items-center gap-2 border-b border-border bg-background px-4 text-sm font-semibold text-foreground">
              <FileText className="h-4 w-4 text-muted-foreground" /> Job &amp; requirements
            </div>
            <div className="p-4">
            {/* Must-haves are a HARD gate — every shortlisted candidate has them; shown as green chips. */}
            <div className="mb-3">
              <div className="mb-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">Must-have <span className="font-normal normal-case text-muted-foreground/80">· cleared the gate</span></div>
              <div className="flex flex-wrap gap-1.5">
                {must.length === 0 ? <span className="text-sm text-muted-foreground">No must-haves.</span> : must.map((s) => (
                  <span key={s} className="inline-flex items-center gap-1 rounded border border-success/40 bg-success/10 px-2 py-0.5 text-xs font-medium text-success"><Check className="h-3 w-3" />{s}</span>
                ))}
              </div>
            </div>
            {nice.length > 0 && (
              <div className="mb-4">
                <div className="mb-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">Nice to have</div>
                <div className="flex flex-wrap gap-1.5">
                  {nice.map((s) => (
                    <span key={s} className={`inline-flex items-center gap-1 rounded border px-2 py-0.5 text-xs ${matches(s) ? 'border-success/40 bg-success/10 text-success' : 'border-border bg-muted/40 text-muted-foreground'}`}>
                      {matches(s) && <Check className="h-3 w-3" />}{s}
                    </span>
                  ))}
                </div>
              </div>
            )}
            <Card>
              <CardContent className="prose prose-sm max-w-none pt-4 dark:prose-invert">
                <div className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">Job description</div>
                {job.jdFormatted ? <Markdown source={job.jdFormatted} /> : <p className="whitespace-pre-wrap text-sm text-muted-foreground">{job.jdRaw ?? 'No description.'}</p>}
              </CardContent>
            </Card>
            </div>
          </div>

          {/* RIGHT — candidate scorecard (reused) + prev/next */}
          <div className="overflow-y-auto">
            <div className="sticky top-0 z-10 flex h-12 items-center justify-between gap-2 border-b border-border bg-background px-4">
              <div className="flex min-w-0 items-center gap-2 text-sm font-semibold text-foreground">
                <Sparkles className="h-4 w-4 shrink-0 text-muted-foreground" /> Candidate review
              </div>
              <div className="flex shrink-0 items-center gap-1">
                <button onClick={() => setShowDebug((v) => !v)} title="How this candidate was scored" className={`inline-flex h-7 items-center gap-1 rounded-md border px-1.5 text-xs ${showDebug ? 'border-primary bg-primary/10 text-primary' : 'border-input bg-card hover:bg-muted/50'}`}><Info className="h-3.5 w-3.5" /> Score</button>
                <button disabled={!prev} onClick={() => prev && goTo(prev.candidateId)} className="inline-flex h-7 items-center rounded-md border border-input bg-card px-1.5 text-xs disabled:opacity-40 enabled:hover:bg-muted/50"><ChevronLeft className="h-3.5 w-3.5" /></button>
                <button disabled={!next} onClick={() => next && goTo(next.candidateId)} className="inline-flex h-7 items-center rounded-md border border-input bg-card px-1.5 text-xs disabled:opacity-40 enabled:hover:bg-muted/50"><ChevronRight className="h-3.5 w-3.5" /></button>
              </div>
            </div>
            {showDebug && <ScoreBreakdown job={job} cand={active} />}
            <CandidateDetail cand={toDrawer(active)} selected={active.selected} onToggleSelect={() => toggleSelect(false)} />
          </div>
        </div>
      </div>
    </DashboardLayout>
  )
}

// Debug/transparency panel: the initial job criteria the screen ran against + this candidate's
// structured sub-scores + final score, so the reviewer can see exactly how the ranking happened.
function ScoreBreakdown({ job, cand }: { job: JobDetail; cand: JobShortlistCandidate }) {
  const r = job.requirements
  const yr = (m?: number) => (m ? Math.round(m / 12) : null)
  const lo = yr(r.minMonths)
  const hi = yr(r.maxMonths)
  // Effective band per the rule: closed "10-12" → ±1y → 9–13; open "10+" → floor + 50% → 10–15.
  const eff = lo == null && hi == null ? null
    : hi != null ? { lo: Math.max(0, lo! - 1), hi: hi + 1 }
    : { lo: lo!, hi: Math.round(lo! * 1.5) }
  const band = eff == null ? 'not specified'
    : `${eff.lo}–${eff.hi} yrs ` + (hi != null ? `(stated ${lo}–${hi}, ±1y)` : `(stated ${lo}+, +50%)`)
  const candY = cand.totalMonthsExp != null ? Math.round(cand.totalMonthsExp / 12) : null
  const expNote = candY == null || eff == null ? '—'
    : candY < eff.lo ? `${eff.lo - candY}y below the effective floor (under-qualified)`
    : candY > eff.hi ? `${candY - eff.hi}y above the effective ceiling (over-qualified → penalised)`
    : 'within the effective band ✓'
  const sub = cand.subScores
  // Weighted model — mirrors packages/core scoring DEFAULT_WEIGHTS. A signal with NO candidate data is
  // DROPPED (not scored 0) and the remaining weights renormalised, so an absent field never lowers the score.
  const W = { skills: 0.32, experience: 0.2, allied: 0.12, notice: 0.16, ctc: 0.1, location: 0.1 }
  type Crit = { label: string; weight: number; value: number; active: boolean }
  const crits: Crit[] = sub ? [
    { label: 'Nice-to-have skills', weight: W.skills, value: sub.skills, active: true },
    { label: 'Experience vs band', weight: W.experience, value: sub.experience, active: true },
    { label: 'Allied skills', weight: W.allied, value: sub.allied, active: true },
    { label: 'Notice / availability', weight: W.notice, value: sub.notice, active: sub.notice > 0 },
    { label: 'CTC fit', weight: W.ctc, value: sub.ctc, active: sub.ctc > 0 },
    { label: 'Location', weight: W.location, value: sub.location, active: sub.location > 0 },
  ] : []
  const activeW = crits.filter((c) => c.active).reduce((a, c) => a + c.weight, 0) || 1
  const weighted = crits.filter((c) => c.active).reduce((a, c) => a + c.weight * c.value, 0) / activeW
  const structured = Math.round(100 * (0.55 + 0.45 * weighted))
  const Bar = ({ c }: { c: Crit }) => (
    <div className={`mb-1.5 flex items-center gap-2 ${c.active ? '' : 'opacity-50'}`}>
      <span className="w-36 shrink-0 text-muted-foreground">{c.label}</span>
      <span className="w-9 shrink-0 text-right text-[10px] text-muted-foreground/70">{Math.round(c.weight * 100)}%</span>
      <div className="h-2 flex-1 overflow-hidden rounded-full bg-muted">{c.active && <div className="h-full rounded-full bg-primary" style={{ width: `${Math.round(Math.max(0, Math.min(1, c.value)) * 100)}%` }} />}</div>
      <span className="w-14 text-right tabular-nums text-foreground">{c.active ? c.value.toFixed(2) : <span className="text-muted-foreground/60">no data</span>}</span>
    </div>
  )
  return (
    <div className="m-4 rounded-lg border border-primary/30 bg-primary/5 p-4 text-xs">
      <div className="mb-3 flex items-center gap-1.5 font-semibold text-primary"><Info className="h-3.5 w-3.5" /> How this candidate was scored</div>

      <div className="mb-3 space-y-0.5 text-foreground">
        <div className="mb-1 font-medium uppercase tracking-wide text-muted-foreground">Job criteria · this candidate</div>
        <div>Experience band <span className="font-semibold">{band}</span> — candidate <span className="font-semibold">{candY != null ? `${candY}y` : '—'}</span> ({expNote})</div>
      </div>

      {/* Must-have hard gate */}
      <div className="mb-3 flex items-center gap-2 rounded-md border border-success/40 bg-success/10 px-2.5 py-1.5">
        <Check className="h-3.5 w-3.5 shrink-0 text-success" /><span className="font-medium text-success">Must-have gate cleared</span>
        <span className="ml-auto truncate text-success/80">{(cand.matched.length ? cand.matched : r.mustHave).join(', ')}</span>
      </div>

      {/* Weighted-model bars */}
      <div className="mb-3">
        <div className="mb-1.5 flex items-center font-medium uppercase tracking-wide text-muted-foreground">Weighted model — signal · weight · score<span className="ml-auto rounded bg-primary/15 px-1.5 py-0.5 text-[10px] font-bold text-primary">final {cand.score}</span></div>
        {crits.map((c) => <Bar key={c.label} c={c} />)}
        <Bar c={{ label: 'Employment type', weight: 0, value: 0, active: false }} />
      </div>

      {/* Derivation of the final score */}
      <div className="space-y-0.5 rounded-md bg-background/70 p-2.5 text-[11px] leading-relaxed">
        <div><span className="font-semibold text-foreground">Structured pre-rank</span> = 55 (gate cleared) + 45 × weighted-active = <span className="font-semibold text-foreground">{structured}</span></div>
        <div><span className="font-semibold text-foreground">AI re-rank (final score)</span> = <span className="font-semibold text-foreground">{cand.score}</span> · {cand.verdict ?? '—'} · {cand.confidence ?? '—'} confidence — refines the pre-rank with domain, seniority &amp; cited evidence.</div>
        <div className="text-muted-foreground">“No data” signals (location / notice / CTC / employment) are excluded &amp; the weights renormalised — an absent field never drags the score down.</div>
      </div>
    </div>
  )
}
